/**
 * @voltxampere/web — `<vxa-search>` (Sprint 11).
 *
 * Sidebar search input. Two-way bound to `sidebarSearchQuery`
 * via the SignalController bridge — typing updates the
 * library filter immediately, and external mutations (e.g.
 * "clear search" command, Sprint 13) reflect back into the
 * input.
 *
 * Sprint 18 will swap the substring filter for a fuzzy match.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { LitElement, html, css } from "lit";
import { customElement } from "lit/decorators.js";

import { sidebarSearchQuery } from "../store/ui.js";
import { currentLocale } from "../store/locale.js";
import { t } from "../i18n/index.js";
import { SignalController } from "../lit-utils/signal-controller.js";

@customElement("vxa-search")
export class VxaSearch extends LitElement {
  private readonly _query = new SignalController(this, sidebarSearchQuery);
  private readonly _locale = new SignalController(this, currentLocale);

  static override styles = css`
    :host {
      display: block;
      padding: var(--s-4);
      border-bottom: 1px solid var(--ivory-wisp);
      flex-shrink: 0;
    }

    input {
      width: 100%;
      background: var(--void);
      border: 1px solid var(--ivory-wisp);
      padding: var(--s-2) var(--s-3);
      font-family: var(--font-mono);
      font-size: var(--fs-small);
      color: var(--ivory);
      outline: none;
      letter-spacing: 0.04em;
      transition:
        border-color var(--transition-hover),
        background var(--transition-hover);
    }

    input:focus {
      border-color: var(--brass-faint);
      background: var(--ink-2);
    }

    input::placeholder {
      color: var(--ivory-faint);
      font-style: italic;
      font-family: var(--font-body);
      letter-spacing: 0;
    }
  `;

  private readonly _onInput = (e: Event): void => {
    const target = e.target as HTMLInputElement;
    this._query.value = target.value;
  };

  override render() {
    return html`
      <input
        type="text"
        .value=${this._query.value}
        placeholder=${t("sidebar.search.placeholder")}
        @input=${this._onInput}
        autocomplete="off"
        spellcheck="false"
      />
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "vxa-search": VxaSearch;
  }
}
